import React, { useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import SearchIcon from "@mui/icons-material/Search";
import { useLazySearchSelectionStudentsQuery } from "../../../services/admin/adminStudents";

const getStudentName = (student, toTitleCase) =>
  `${toTitleCase(student?.first_name || "")} ${toTitleCase(
    student?.last_name || ""
  )}`.trim() || `Student #${student?.id}`;

const normalizeResults = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.students)) return response.students;
  return [];
};

const FIELD_LABELS = {
  first_name: "First name",
  last_name: "Last name",
  email: "Email",
  school: "School",
  city_name: "City",
  grad_year: "Grad year",
  GPA: "GPA",
  GradePercent: "Grade percent",
  Field_of_Study: "Field of study",
  PS_Program_Name: "Program",
  PS_Path: "Post-secondary path",
  career_job_direction: "Career direction",
  bursary_reason: "Bursary reason",
  bursary_total_income: "Household income",
  affiliations: "Affiliations",
  awardHistory: "Award history",
};

const SEARCHABLE_FIELDS = [
  "first_name",
  "last_name",
  "email",
  "school",
  "city_name",
  "grad_year",
  "GPA",
  "GradePercent",
  "Field_of_Study",
  "PS_Program_Name",
  "PS_Path",
  "career_job_direction",
  "bursary_reason",
  "bursary_total_income",
  "affiliations",
];

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const getSearchTerms = (query) =>
  query
    .trim()
    .split(/\s+/)
    .filter((term) => term.length >= 2);

const stringifyFieldValue = (value) => {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) {
    return value
      .map((entry) => {
        if (!entry || typeof entry !== "object") return String(entry || "");
        return [
          entry.scholarship_name,
          entry.award_amount,
          entry.award_year,
        ]
          .filter(Boolean)
          .join(" ");
      })
      .filter(Boolean)
      .join(" | ");
  }

  return String(value);
};

const getContextSnippet = (value, terms) => {
  const text = stringifyFieldValue(value).replace(/\s+/g, " ").trim();
  if (!text) return "";

  const lowerText = text.toLowerCase();
  const firstMatchIndex = terms.reduce((bestIndex, term) => {
    const index = lowerText.indexOf(term.toLowerCase());
    if (index === -1) return bestIndex;
    return bestIndex === -1 ? index : Math.min(bestIndex, index);
  }, -1);

  if (firstMatchIndex === -1) return "";

  const start = Math.max(0, firstMatchIndex - 36);
  const end = Math.min(text.length, firstMatchIndex + 72);
  return `${start > 0 ? "... " : ""}${text.slice(start, end)}${
    end < text.length ? " ..." : ""
  }`;
};

const highlightMatches = (text, terms) => {
  if (!text || terms.length === 0) return text;

  const regex = new RegExp(`(${terms.map(escapeRegExp).join("|")})`, "ig");
  return text.split(regex).map((part, index) => {
    const isMatch = terms.some(
      (term) => part.toLowerCase() === term.toLowerCase()
    );

    return isMatch ? (
      <Box
        key={`${part}-${index}`}
        component="mark"
        sx={{
          px: 0.25,
          py: 0,
          borderRadius: 0.5,
          backgroundColor: "#fff3a3",
          color: "inherit",
        }}
      >
        {part}
      </Box>
    ) : (
      part
    );
  });
};

const normalizeBackendMatches = (student) => {
  if (Array.isArray(student.matches)) {
    return student.matches
      .map((match) => ({
        field: match.field || match.key || match.name,
        label: match.label,
        value: match.value || match.snippet || match.text || "",
      }))
      .filter((match) => match.field || match.value);
  }

  if (student.matched_fields && typeof student.matched_fields === "object") {
    return Object.entries(student.matched_fields).map(([field, value]) => ({
      field,
      value,
    }));
  }

  return [];
};

const getMatchDetails = (student, query) => {
  const terms = getSearchTerms(query);
  if (terms.length === 0) return [];

  const backendMatches = normalizeBackendMatches(student);
  if (backendMatches.length > 0) {
    return backendMatches.slice(0, 4).map((match) => ({
      label: match.label || FIELD_LABELS[match.field] || match.field || "Match",
      snippet:
        getContextSnippet(match.value, terms) ||
        stringifyFieldValue(match.value).slice(0, 120),
    }));
  }

  const fieldMatches = SEARCHABLE_FIELDS.map((field) => ({
    field,
    snippet: getContextSnippet(student[field], terms),
  })).filter((match) => match.snippet);

  const awardSnippet = getContextSnippet(student.awardHistory, terms);
  if (awardSnippet) {
    fieldMatches.push({ field: "awardHistory", snippet: awardSnippet });
  }

  return fieldMatches.slice(0, 4).map((match) => ({
    label: FIELD_LABELS[match.field] || match.field,
    snippet: match.snippet,
  }));
};

const StudentSearchDialog = ({
  open,
  onClose,
  cityId,
  selectedStudents,
  onSelectStudent,
  toTitleCase,
}) => {
  const [searchText, setSearchText] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [searchStudents, { data, isFetching, error, isUninitialized }] =
    useLazySearchSelectionStudentsQuery();

  const trimmedSearchText = searchText.trim();
  const canSearch = trimmedSearchText.length >= 2;
  const canShowResults = canSearch && hasSearched;
  const results = useMemo(
    () => (canShowResults ? normalizeResults(data) : []),
    [canShowResults, data]
  );
  const selectedStudentIds = useMemo(
    () => new Set(selectedStudents.map((student) => student.id)),
    [selectedStudents]
  );

  useEffect(() => {
    if (!open) {
      setSearchText("");
      setHasSearched(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;

    if (!canSearch) {
      setHasSearched(false);
      return;
    }

    const timer = window.setTimeout(() => {
      setHasSearched(true);
      searchStudents({ q: trimmedSearchText, city_id: cityId, limit: 25 });
    }, 300);

    return () => window.clearTimeout(timer);
  }, [canSearch, cityId, open, searchStudents, trimmedSearchText]);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!canSearch) return;

    setHasSearched(true);
    searchStudents({ q: trimmedSearchText, city_id: cityId, limit: 25 });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Search Students</DialogTitle>
      <DialogContent dividers>
        <Box component="form" onSubmit={handleSubmit}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <TextField
              autoFocus
              fullWidth
              label="Search all accessible student info"
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              helperText="Search name, email, school, application details, affiliations, award history, and other indexed fields."
            />
            <Button
              type="submit"
              variant="contained"
              startIcon={<SearchIcon />}
              disabled={!canSearch || isFetching}
              sx={{ minWidth: 120, alignSelf: { sm: "flex-start" } }}
            >
              Search
            </Button>
          </Stack>
        </Box>

        <Box sx={{ minHeight: 320, mt: 2 }}>
          {isFetching && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert severity="error">
              Student search is unavailable. The backend search endpoint may not
              be wired up yet.
            </Alert>
          )}

          {!isFetching && !error && !canSearch && (
            <Typography color="text.secondary" sx={{ pt: 2 }}>
              Enter at least 2 characters to search.
            </Typography>
          )}

          {!isFetching &&
            !error &&
            canShowResults &&
            !isUninitialized &&
            results.length === 0 && (
              <Typography color="text.secondary" sx={{ pt: 2 }}>
                No matching students found.
              </Typography>
            )}

          {!isFetching && !error && results.length > 0 && (
            <List disablePadding>
              {results.map((student, index) => {
                const isSelected = selectedStudentIds.has(student.id);
                const totalAwarded = student.awardHistory?.reduce(
                  (sum, award) => sum + (Number(award?.award_amount) || 0),
                  0
                );
                const matchDetails = getMatchDetails(student, searchText);
                const searchTerms = getSearchTerms(searchText);

                return (
                  <React.Fragment key={student.id}>
                    {index > 0 && <Divider component="li" />}
                    <ListItem
                      disablePadding
                      sx={{
                        alignItems: "flex-start",
                        gap: 2,
                        px: 2,
                        py: 1.5,
                      }}
                      secondaryAction={
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => onSelectStudent(student)}
                          disabled={isSelected}
                          sx={{ whiteSpace: "nowrap" }}
                        >
                          {isSelected ? "Selected" : "Nominate Student"}
                        </Button>
                      }
                    >
                        <ListItemText
                          sx={{ pr: { xs: 0, sm: 19 } }}
                          primary={
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              flexWrap="wrap"
                              useFlexGap
                            >
                              <Typography fontWeight={700}>
                                {getStudentName(student, toTitleCase)}
                              </Typography>
                              {student.school && (
                                <Chip size="small" label={student.school} />
                              )}
                              {student.grad_year && (
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label={`Grad ${student.grad_year}`}
                                />
                              )}
                              {isSelected && (
                                <Chip size="small" label="Selected" />
                              )}
                            </Stack>
                          }
                          secondary={
                            <Box sx={{ mt: 0.75 }}>
                              <Typography
                                component="span"
                                variant="body2"
                                color="text.secondary"
                              >
                                {[student.email, student.city_name]
                                  .filter(Boolean)
                                  .join(" | ")}
                              </Typography>
                              {student.awardHistory?.length > 0 && (
                                <Typography variant="caption" color="success.main">
                                  {student.awardHistory.length} award
                                  {student.awardHistory.length > 1 ? "s" : ""} (
                                  ${totalAwarded.toLocaleString()})
                                </Typography>
                              )}
                              {matchDetails.length > 0 && (
                                <Box
                                  sx={{
                                    mt: 1,
                                    display: "grid",
                                    gap: 0.5,
                                  }}
                                >
                                  {matchDetails.map((match, matchIndex) => (
                                    <Typography
                                      key={`${match.label}-${matchIndex}`}
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ display: "block" }}
                                    >
                                      <Box
                                        component="span"
                                        sx={{ fontWeight: 700, color: "text.primary" }}
                                      >
                                        {match.label}:
                                      </Box>{" "}
                                      {highlightMatches(
                                        match.snippet,
                                        searchTerms
                                      )}
                                    </Typography>
                                  ))}
                                </Box>
                              )}
                            </Box>
                          }
                        />
                    </ListItem>
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default StudentSearchDialog;
